import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '/api/v1', // Proxied to backend via Vite
  timeout: 30000, // Increased to 30s for ONE Line search (chunked API calls can take 14s+)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token when available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', response.config.url, response.data);
    }
    return response.data;
  },
  (error) => {
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Token is invalid or expired
      localStorage.removeItem('auth_token');
      
      // Only redirect to login if we're not already on auth pages
      if (!window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/auth')) {
        window.location.href = '/login';
      }
    }
    
    // Log error details for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
    }
    
    return Promise.reject(error);
  }
);

// Dashboard API endpoints  
export const dashboardApi = {
  // Get comprehensive dashboard summary with all sections
  getSummary: () => {
    return api.get('/dashboard/summary');
  },

  // Get metrics (legacy)
  getMetrics: () => {
    return api.get('/dashboard/metrics');
  },

  // Get shipment tracking data
  getShipmentTracking: (params?: {
    limit?: number;
    status?: 'all' | 'preparing' | 'in-transit' | 'arrived' | 'delivered';
  }) => {
    return api.get('/dashboard/shipment-tracking', { params });
  },
};

// Workflow API endpoints
export const workflowApi = {
  getDashboard: (filters?: {
    status?: string[];
    priority?: string[];
    taskType?: string[];
    dateRange?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status.join(','));
    if (filters?.priority) params.append('priority', filters.priority.join(','));
    if (filters?.taskType) params.append('taskType', filters.taskType.join(','));
    if (filters?.dateRange) params.append('dateRange', filters.dateRange);
    
    // Use workflow endpoint with real data
    return api.get(`/workflow/dashboard?${params.toString()}`);
  },

  getTask: (taskId: string) => {
    return api.get(`/workflow/tasks/${taskId}`);
  },

  transitionTask: (taskId: string, event: string, data?: any, comment?: string) => {
    return api.post(`/workflow/tasks/${taskId}/transition`, {
      event,
      data,
      comment,
    });
  },

  createEmailDraft: (taskId: string, draft: {
    recipient: string;
    ccEmails?: string[];
    subject: string;
    body: string;
    attachments?: Array<{ filename: string; url: string }>;
  }) => {
    return api.post(`/workflow/tasks/${taskId}/draft`, draft);
  },

  sendEmailDraft: (draftId: string, options?: {
    sendImmediately?: boolean;
    scheduleFor?: string;
  }) => {
    return api.post(`/workflow/drafts/${draftId}/send`, options || {});
  },

  getUrgentPickups: () => {
    return api.get('/workflow/urgent-pickups');
  },

  getIntegrationHealth: () => {
    return api.get('/workflow/integrations/health');
  },

  recalculatePriorities: (taskIds?: string[], notifyChanges?: boolean) => {
    return api.post('/workflow/recalculate-priorities', {
      taskIds,
      notifyChanges,
    });
  },

  getShipmentTracking: (params?: {
    limit?: number;
    status?: 'all' | 'preparing' | 'in-transit' | 'arrived' | 'delivered';
  }) => {
    // Use dashboard endpoint with real data - removed fallback to workflow mock data
    return api.get('/dashboard/shipment-tracking', { params });
  },

  // Booking request methods
  getBookingRequestPreview: (shipmentId: string, recipientEmail?: string) => {
    const params = recipientEmail ? { recipientEmail } : {};
    return api.get(`/workflow/shipments/${shipmentId}/booking-request-preview`, { params });
  },

  // Shipping instruction methods
  getShippingInstructionPreview: (shipmentId: string) => {
    return api.get('/email-drafts/preview', {
      params: {
        shipmentId,
        draftType: 'shipping_instruction'
      }
    }).then((response: any) => response.email);
  },

  sendBookingRequest: (
    shipmentId: string,
    emailData?: {
      to?: string;
      cc?: string;
      subject?: string;
      body?: string;
    }
  ) => {
    return api.post(`/workflow/shipments/${shipmentId}/send-booking-request`, emailData || {});
  },
};

// Contracts API endpoints
export const contractsApi = {
  getContracts: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  }) => {
    return api.get('/contracts', { params });
  },

  getContract: (contractId: string) => {
    return api.get(`/contracts/${contractId}`);
  },

  createContract: (contract: any) => {
    return api.post('/contracts', contract);
  },

  updateContract: (contractId: string, updates: any) => {
    return api.patch(`/contracts/${contractId}`, updates);
  },

  deleteContract: (contractId: string) => {
    return api.delete(`/contracts/${contractId}`);
  },

  duplicateContract: (contractRef: string): Promise<{ id: string; reference: string; sourceReference: string; message: string }> => {
    return api.post(`/contracts/${contractRef}/duplicate`);
  },

  getContractShipments: (contractId: string) => {
    return api.get(`/contracts/${contractId}/shipments`);
  },

  getContractFilters: () => {
    return api.get('/contracts/filters');
  },

  getNextReference: (supplierCode: string): Promise<{
    supplierCode: string;
    nextSequence: number;
    options: { value: string; year: string; label: string }[];
  }> => {
    return api.get(`/contracts/next-reference/${supplierCode}`);
  },

  // Shipment update by ID
  updateShipment: (shipmentId: string, updates: {
    status?: string;
    booking_number?: string;
    start?: string;
    end?: string;
    etd?: string;
    eta?: string;
    port_departure_code?: string;
    port_arrival_code?: string;
    shipment_note_one?: string;
    shipment_note_two?: string;
    packing_notes?: string;
  }) => {
    return api.patch(`/shipments/id/${shipmentId}`, updates);
  },

  // Container update by ID
  updateContainer: (containerId: string, updates: {
    status?: string;
    container_number?: string;
    seal_number?: string;
    equipment_type?: string;
    net_weight?: number;
    gross_weight?: number;
  }) => {
    return api.patch(`/containers/id/${containerId}`, updates);
  },

  // Goods update by ID
  updateGood: (goodId: string, updates: {
    commodity_code?: string;
    quantity?: number;
    unit?: string;
    net_weight?: number;
    description?: string;
    variety?: string;
    grade?: string;
    size?: string;
    spec?: string;
    price?: number;
    buyer_price?: number;
    num_units?: number;
    lbs_ea?: number;
  }) => {
    return api.patch(`/goods/${goodId}`, updates);
  },

  /**
   * Generate and download contract PDF
   * @param contractId - Contract ID to generate PDF for
   * @param audience - 'seller' or 'buyer' version
   * @returns Promise<Blob> - PDF file as blob for download
   */
  downloadContractPDF: async (
    contractId: string,
    audience: 'seller' | 'buyer'
  ): Promise<Blob> => {
    try {
      const response: any = await api.post('/pdf/contracts/generate', {
        contractId,
        audience,
        returnBuffer: true,
      });

      // Response contains base64 encoded PDF in 'content' field
      // Note: axios interceptor returns response.data, so response is the data object
      if (!response.content) {
        throw new Error('No PDF content returned from server');
      }

      // Convert base64 to Blob
      const binaryString = window.atob(response.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      return new Blob([bytes], { type: 'application/pdf' });
    } catch (error: any) {
      console.error('Failed to download contract PDF:', error);
      throw new Error(
        error.response?.data?.message ||
        'Failed to generate contract PDF. Please try again.'
      );
    }
  },
};

// Health check endpoint
export const healthApi = {
  check: () => {
    return api.get('/health');
  },
};

// Reconciliation API endpoints
export const reconciliationApi = {
  // Get parsed transactions from bank statement PDFs
  getTransactions: (params?: {
    startDate?: string;
    endDate?: string;
    status?: 'pending' | 'matched' | 'reconciled';
    direction?: 'incoming' | 'outgoing';
    limit?: number;
  }) => {
    return api.get('/reconciliation/transactions', { params });
  },

  // Get a single transaction by TRN
  getTransaction: (trn: string) => {
    return api.get(`/reconciliation/transactions/${trn}`);
  },

  // List available bank statement PDF files
  getFiles: (params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    return api.get('/reconciliation/files', { params });
  },

  // Get transactions from a specific date's bank statement
  getFileByDate: (date: string) => {
    return api.get(`/reconciliation/files/${date}`);
  },

  // Match a transaction to shipments
  matchTransaction: (transaction: any, options?: {
    partyFilter?: boolean;
    dateWindowDays?: number;
    multiShipmentSearch?: boolean;
    maxCombinationSize?: number;
  }) => {
    return api.post('/reconciliation/match', { transaction, options });
  },

  // Match a transaction by TRN (fetches from PDF first)
  matchByTrn: (trn: string, options?: {
    partyFilter?: boolean;
    dateWindowDays?: number;
    multiShipmentSearch?: boolean;
    maxCombinationSize?: number;
  }) => {
    return api.post('/reconciliation/match-by-trn', { trn, options });
  },

  // Health check for reconciliation services
  healthCheck: () => {
    return api.get('/reconciliation/health');
  },

  // Look up invoice by contract/shipment reference
  lookupInvoice: (reference: string) => {
    return api.get(`/reconciliation/invoice/${encodeURIComponent(reference)}`);
  },

  // Create invoice from shipment if it doesn't exist in Xero
  createInvoice: (shipmentId: string) => {
    return api.post('/reconciliation/invoice', { shipmentId });
  },

  // Reconcile a transaction with matched shipment(s) and create payment in Xero
  reconcileTransaction: (
    transaction: any,
    match: any,
    paymentType: 'full' | 'partial' | 'credit' | 'fee',
    notes?: string
  ) => {
    return api.post('/reconciliation/reconcile', {
      transaction,
      match,
      paymentType,
      notes
    });
  },

  // Create credit note for adjustment scenarios
  createCreditNote: (data: {
    originalInvoiceId: string;
    amount: number;
    reference: string;
    description: string;
    date?: string;
  }) => {
    return api.post('/reconciliation/credit-note', data);
  },

  // Get unreconciled bank transactions from Xero
  getUnreconciledBankTransactions: () => {
    return api.get('/reconciliation/bank-transactions');
  },

  // Mark bank statement line as reconciled with payment
  reconcileBankStatement: (statementLineId: string, paymentId: string) => {
    return api.post('/reconciliation/bank-reconcile', {
      statementLineId,
      paymentId
    });
  },

  // Import a single parsed transaction with deduplication
  importTransaction: (transaction: any, options?: {
    skipExactDuplicates?: boolean;
    flagPartialMatches?: boolean;
  }) => {
    return api.post('/reconciliation/import-transaction', {
      transaction,
      ...options
    });
  },

  // Bulk import multiple parsed transactions with deduplication
  importTransactions: (transactions: any[], options?: {
    skipExactDuplicates?: boolean;
    flagPartialMatches?: boolean;
  }) => {
    return api.post('/reconciliation/import-transactions', {
      transactions,
      ...options
    });
  },

  // Payment Pairs API
  // Get list of payment pairs with filtering
  getPairs: (params?: {
    status?: 'UNPAID' | 'BUYER_PAID' | 'SUPPLIER_PAID' | 'FULLY_SETTLED';
    revenueType?: 'COMMISSION' | 'MARGIN' | 'MARGIN_PLUS_COMMISSION';
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/reconciliation/pairs', { params });
  },

  // Get payment pair for a specific shipment
  getPair: (shipmentId: string) => {
    return api.get(`/reconciliation/pairs/${shipmentId}`);
  },

  // Get payment pairs summary counts
  getPairsSummary: () => {
    return api.get('/reconciliation/pairs/summary');
  },

  // Get timing analytics
  getTimingAnalytics: (params?: {
    groupBy?: 'buyer' | 'supplier';
    startDate?: string;
    endDate?: string;
  }) => {
    return api.get('/reconciliation/analytics/timing', { params });
  },
};

// Shipment Transactions API (new payment tracking model)
export const transactionsApi = {
  // List all transactions with optional filtering
  list: (params?: {
    shipmentId?: string;
    direction?: 'BUYER_RECEIVABLE' | 'SUPPLIER_PAYABLE' | 'COMMISSION';
    status?: 'PENDING' | 'PAID' | 'PARTIAL' | 'VOIDED';
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/transactions', { params });
  },

  // Get a single transaction
  get: (transactionId: string) => {
    return api.get(`/transactions/${transactionId}`);
  },

  // Create a new transaction
  create: (data: {
    shipmentId: string;
    transactionType: 'INVOICE' | 'PAYMENT' | 'CREDIT_NOTE';
    direction: 'BUYER_RECEIVABLE' | 'SUPPLIER_PAYABLE' | 'COMMISSION';
    amount: number;
    date: string;
    dueDate?: string;
    paymentMethod?: 'WIRE' | 'CHECK' | 'ACH' | null;
    reference?: string;
    description?: string;
    xeroId?: string;
  }) => {
    return api.post('/transactions', data);
  },

  // Update a transaction
  update: (transactionId: string, data: {
    transactionType?: 'INVOICE' | 'PAYMENT' | 'CREDIT_NOTE';
    direction?: 'BUYER_RECEIVABLE' | 'SUPPLIER_PAYABLE' | 'COMMISSION';
    amount?: number;
    date?: string;
    dueDate?: string;
    paymentMethod?: 'WIRE' | 'CHECK' | 'ACH' | null;
    reference?: string;
    description?: string;
    status?: 'PENDING' | 'PAID' | 'PARTIAL' | 'VOIDED';
  }) => {
    return api.patch(`/transactions/${transactionId}`, data);
  },

  // Delete a transaction
  delete: (transactionId: string) => {
    return api.delete(`/transactions/${transactionId}`);
  },

  // Get payment summary for a shipment
  getSummary: (shipmentId: string) => {
    return api.get(`/transactions/summary/${shipmentId}`);
  },

  // Get payment summaries for multiple shipments (with optional filters)
  getSummaries: (params?: {
    contractId?: string;
    status?: 'UNPAID' | 'BUYER_PARTIAL' | 'BUYER_PAID' | 'SUPPLIER_PARTIAL' | 'FULLY_SETTLED';
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/transactions/summaries', { params });
  },
};

// Bank Transactions API (new two-tier payment architecture)
export const bankTransactionsApi = {
  // List bank transactions with optional filtering
  list: (params?: {
    status?: 'UNRECONCILED' | 'PARTIAL' | 'RECONCILED' | 'PUSHED';
    transactionType?: 'WIRE_IN' | 'WIRE_OUT' | 'CHECK_IN' | 'CHECK_OUT' | 'ACH_IN' | 'ACH_OUT';
    counterparty?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/bank-transactions', { params });
  },

  // Get a single bank transaction with allocations
  get: (id: string) => {
    return api.get(`/bank-transactions/${id}`);
  },

  // Get summary counts by status
  getSummary: () => {
    return api.get('/bank-transactions/summary');
  },

  // Create a new bank transaction
  create: (data: {
    transactionType: 'WIRE_IN' | 'WIRE_OUT' | 'CHECK_IN' | 'CHECK_OUT' | 'ACH_IN' | 'ACH_OUT';
    amount: number;
    date: string;
    reference?: string;
    bankAccount?: string;
    counterparty?: string;
    description?: string;
    sourceType?: string;
    sourceFile?: string;
    currencyCode?: string;
  }) => {
    return api.post('/bank-transactions', data);
  },

  // Update a bank transaction
  update: (id: string, data: {
    transactionType?: 'WIRE_IN' | 'WIRE_OUT' | 'CHECK_IN' | 'CHECK_OUT' | 'ACH_IN' | 'ACH_OUT';
    amount?: number;
    date?: string;
    reference?: string | null;
    bankAccount?: string | null;
    counterparty?: string | null;
    description?: string | null;
    currencyCode?: string;
    status?: 'UNRECONCILED' | 'PARTIAL' | 'RECONCILED' | 'PUSHED';
  }) => {
    return api.put(`/bank-transactions/${id}`, data);
  },

  // Delete a bank transaction
  delete: (id: string) => {
    return api.delete(`/bank-transactions/${id}`);
  },

  // Add an allocation to a transaction
  addAllocation: (transactionId: string, data: {
    shipmentId: string;
    amount: number;
    allocationType: 'INVOICE_PAYMENT' | 'COMMISSION' | 'CREDIT_APPLIED' | 'DEPOSIT' | 'ADJUSTMENT' | 'OVERPAYMENT_CREDIT';
    xeroInvoiceId?: string;
    notes?: string;
  }) => {
    return api.post(`/bank-transactions/${transactionId}/allocations`, data);
  },

  // Update an allocation
  updateAllocation: (transactionId: string, allocationId: string, data: {
    amount?: number;
    allocationType?: 'INVOICE_PAYMENT' | 'COMMISSION' | 'CREDIT_APPLIED' | 'DEPOSIT' | 'ADJUSTMENT' | 'OVERPAYMENT_CREDIT';
    xeroInvoiceId?: string | null;
    notes?: string | null;
  }) => {
    return api.put(`/bank-transactions/${transactionId}/allocations/${allocationId}`, data);
  },

  // Remove an allocation
  removeAllocation: (transactionId: string, allocationId: string) => {
    return api.delete(`/bank-transactions/${transactionId}/allocations/${allocationId}`);
  },

  // Validate allocations
  validate: (id: string) => {
    return api.get(`/bank-transactions/${id}/validate`);
  },

  // Mark as reconciled
  reconcile: (id: string) => {
    return api.post(`/bank-transactions/${id}/reconcile`);
  },

  // Preview Xero push
  previewXeroPush: (id: string) => {
    return api.get(`/bank-transactions/${id}/preview-xero`);
  },

  // Push to Xero
  pushToXero: (id: string) => {
    return api.post(`/bank-transactions/${id}/push-to-xero`);
  },

  getXeroStatus: () => {
    return api.get('/bank-transactions/xero-status');
  },

  getXeroReconcileLink: (bankAccountId?: string) => {
    return api.get('/bank-transactions/xero-reconcile-link', { params: { bankAccountId } });
  },

  // Get transactions flagged for review (potential duplicates)
  getNeedsReview: (limit?: number) => {
    return api.get('/bank-transactions/needs-review', { params: { limit } });
  },

  // Resolve a review flag (keep or delete the transaction)
  resolveReview: (id: string, resolution: 'keep' | 'delete') => {
    return api.post(`/bank-transactions/${id}/resolve-review`, { resolution });
  },

  // Convenience method for wire entry
  createWire: (data: {
    direction: 'IN' | 'OUT';
    amount: number;
    date: string;
    reference: string;
    counterparty: string;
    shipmentId?: string;
    description?: string;
  }) => {
    return api.post('/bank-transactions/wires', data);
  },

  uploadCheckImage: (id: string, data: {
    imageBase64: string;
    filename: string;
    mimeType?: string;
  }) => {
    return api.post(`/bank-transactions/${id}/check-image`, data);
  },

  deleteCheckImage: (id: string) => {
    return api.delete(`/bank-transactions/${id}/check-image`);
  },

  getHistory: (id: string) => {
    return api.get(`/bank-transactions/${id}/history`);
  },
};

// Credits API (claims, returns, overpayments)
export const creditsApi = {
  // List credits with optional filtering
  list: (params?: {
    partyType?: 'BUYER' | 'SUPPLIER';
    partyId?: string;
    partyName?: string;
    creditType?: 'CLAIM' | 'RETURN' | 'OVERPAYMENT' | 'PRICING_ADJUSTMENT' | 'OTHER';
    status?: 'APPROVED' | 'PARTIAL' | 'EXHAUSTED' | 'VOIDED';
    originShipmentId?: string;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/credits', { params });
  },

  // Get available credits for reconciliation
  getAvailable: (params?: {
    partyType?: 'BUYER' | 'SUPPLIER';
    partyId?: string;
    partyName?: string;
    creditType?: 'CLAIM' | 'RETURN' | 'OVERPAYMENT' | 'PRICING_ADJUSTMENT' | 'OTHER';
    minAmount?: number;
  }) => {
    return api.get('/credits/available', { params });
  },

  // Get credit summary
  getSummary: () => {
    return api.get('/credits/summary');
  },

  // Get a single credit
  get: (id: string) => {
    return api.get(`/credits/${id}`);
  },

  // Create a new credit
  create: (data: {
    originShipmentId: string;
    partyType: 'BUYER' | 'SUPPLIER';
    partyId?: string;
    partyName: string;
    creditType: 'CLAIM' | 'RETURN' | 'OVERPAYMENT' | 'PRICING_ADJUSTMENT' | 'OTHER';
    originalAmount: number;
    currencyCode?: string;
    description?: string;
    referenceNumber?: string;
  }) => {
    return api.post('/credits', data);
  },

  // Void a credit
  void: (id: string, reason?: string) => {
    return api.post(`/credits/${id}/void`, { reason });
  },

  // Apply credit to an allocation
  apply: (creditId: string, data: {
    allocationId: string;
    amount: number;
    appliedBy?: string;
  }) => {
    return api.post(`/credits/${creditId}/apply`, data);
  },

  // Remove a credit application
  removeApplication: (applicationId: string) => {
    return api.delete(`/credits/applications/${applicationId}`);
  },

  // Get credits for a specific party
  getForParty: (partyType: 'BUYER' | 'SUPPLIER', partyId: string) => {
    return api.get(`/credits/party/${partyType}/${partyId}`);
  },
};

// Xero Integration API
export const xeroApi = {
  // Check Xero connection health
  getHealth: () => {
    return api.get('/xero/health');
  },

  // Get authorization URL to connect/reconnect Xero
  getAuthUrl: (returnUrl?: string) => {
    return api.get('/xero/auth', { params: { returnUrl } });
  },

  // Handle OAuth callback (called after redirect)
  handleCallback: (code: string, state: string) => {
    return api.get('/xero/callback', { params: { code, state } });
  },

  // Sync invoices from Xero
  syncInvoices: () => {
    return api.post('/xero/sync');
  },

  // Sync transactions from Xero
  syncTransactions: (options?: {
    startDate?: string;
    endDate?: string;
    dryRun?: boolean;
    skipExisting?: boolean;
  }) => {
    return api.post('/xero/sync/transactions', options);
  },

  // Get sync status
  getSyncStatus: () => {
    return api.get('/xero/sync/status');
  },

  // Preview sync (dry run)
  previewSync: (params?: {
    startDate?: string;
    endDate?: string;
    days?: number;
  }) => {
    return api.get('/xero/sync/preview', { params });
  },

  // Get unmatched invoices
  getUnmatchedInvoices: () => {
    return api.get('/xero/invoices/unmatched');
  },

  // Manually match invoice to shipment
  matchInvoice: (xeroInvoiceId: string, shipmentId: string) => {
    return api.post('/xero/invoices/match', { xeroInvoiceId, shipmentId });
  },

  // Re-run matching for a specific invoice
  rematchInvoice: (xeroInvoiceId: string) => {
    return api.post('/xero/invoices/rematch', { xeroInvoiceId });
  },

  // Ignore invoice (for samples, fees, non-shipment invoices)
  ignoreInvoice: (xeroInvoiceId: string, reason?: string) => {
    return api.post(`/xero/invoices/${xeroInvoiceId}/ignore`, { reason });
  },

  // Unignore invoice (restore to unmatched list)
  unignoreInvoice: (xeroInvoiceId: string) => {
    return api.post(`/xero/invoices/${xeroInvoiceId}/unignore`);
  },

  // Get payment status for a shipment
  getShipmentPaymentStatus: (shipmentId: string) => {
    return api.get(`/xero/shipments/${shipmentId}/payment-status`);
  },

  // Get invoices for a shipment
  getShipmentInvoices: (shipmentId: string) => {
    return api.get(`/xero/shipments/${shipmentId}/invoices`);
  },

  getBankAccounts: () => {
    return api.get<{
      accounts: Array<{
        id: string;
        code: string | null;
        name: string;
        bankAccountNumber: string | null;
        bankAccountType: string | null;
        currencyCode: string | null;
        enablePaymentsToAccount: boolean;
      }>;
    }>('/xero/bank-accounts');
  },
};

// Pending Updates API (Data Confirmation)
export const pendingUpdatesApi = {
  // Get count of pending updates
  getCount: () => {
    return api.get<{ count: number }>('/pending-updates/count');
  },

  // Get next pending update(s) for review (grouped by shipment/contract)
  getNext: () => {
    return api.get('/pending-updates/next');
  },

  // Get a single pending update by ID
  get: (id: string) => {
    return api.get(`/pending-updates/${id}`);
  },

  // List pending updates with filters
  list: (params?: {
    status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'APPLIED' | 'FAILED';
    shipmentId?: string;
    contractId?: string;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/pending-updates', { params });
  },

  // Process a pending update with per-field decisions
  process: (
    id: string,
    data: {
      fieldDecisions: Array<{
        entityType: string;
        entityId: string;
        fieldName: string;
        decision: 'accept' | 'reject' | 'skip';
        editedValue?: unknown;
      }>;
      skipBlockedAction?: boolean;
      reviewedBy: string;
      reviewNotes?: string;
    }
  ) => {
    return api.post(`/pending-updates/${id}/process`, data);
  },

  // Approve entire pending update (legacy)
  approve: (
    id: string,
    data: {
      reviewedBy: string;
      reviewNotes?: string;
      applyChanges?: boolean;
      triggerAction?: boolean;
    }
  ) => {
    return api.post(`/pending-updates/${id}/approve`, data);
  },

  // Reject entire pending update (legacy)
  reject: (
    id: string,
    data: {
      reviewedBy: string;
      reviewNotes?: string;
    }
  ) => {
    return api.post(`/pending-updates/${id}/reject`, data);
  },

  // Get stats by status
  getStats: () => {
    return api.get('/pending-updates/stats');
  },
};

// Pending Invoices API endpoints
export const pendingInvoicesApi = {
  list: (status?: string) => api.get('/invoices/pending', { params: { status } }),
  getCounts: () => api.get('/invoices/pending/counts'),
  getById: (id: string) => api.get(`/invoices/pending/${id}`),
  update: (id: string, data: { invoiceDate?: string; dueDate?: string; reference?: string }) => 
    api.patch(`/invoices/pending/${id}`, data),
  updateLineItems: (id: string, lineItems: any[]) => api.patch(`/invoices/pending/${id}/line-items`, { lineItems }),
  addLineItem: (id: string, lineItem: any) => api.post(`/invoices/pending/${id}/line-items`, lineItem),
  deleteLineItem: (id: string, lineItemId: string) => api.delete(`/invoices/pending/${id}/line-items/${lineItemId}`),
  toggleLineItem: (id: string, lineItemId: string) => api.post(`/invoices/pending/${id}/line-items/${lineItemId}/toggle`),
  sendToXero: (id: string) => api.post(`/invoices/pending/${id}/send`),
  delete: (id: string) => api.delete<{ success: boolean }>(`/invoices/pending/${id}`),
  
  createFromShipmentCustomer: (shipmentId: string) => 
    api.post<{ pendingInvoiceId: string }>('/invoices/pending/from-shipment/customer', { shipmentId }),
  createFromShipmentCommission: (shipmentId: string) => 
    api.post<{ pendingInvoiceId: string }>('/invoices/pending/from-shipment/commission', { shipmentId }),
  getByShipments: (shipmentIds: string[]) =>
    api.post<Record<string, { 
      exists: boolean; 
      pendingInvoiceId?: string; 
      status?: string; 
      xeroInvoiceId?: string;
      hasDiscrepancy?: boolean;
      amountDiscrepancy?: number;
    }>>('/invoices/pending/by-shipments', { shipmentIds }),
  
  getReconciliation: (id: string) => 
    api.get<{
      pendingInvoiceId: string;
      xeroInvoiceId: string;
      xeroStatus: string;
      canPushToXero: boolean;
      xeroTotal: number;
      atlasTotal: number;
      discrepancy: number;
      comparison: {
        matched: Array<{ atlasItem: any; xeroItem: any; matchScore: number }>;
        atlasOnly: Array<any>;
        xeroOnly: Array<any>;
      };
    }>(`/invoices/pending/${id}/reconcile`),
  
  saveXeroItemsToAtlas: (id: string, xeroItemIds: string[]) =>
    api.post<{ success: boolean; itemsAdded: number; newTotal: number }>(
      `/invoices/pending/${id}/reconcile/save-to-atlas`, 
      { xeroItemIds }
    ),
  
  pushAtlasItemsToXero: (id: string, atlasItemIds: string[]) =>
    api.post<{ success: boolean } | { error: string }>(
      `/invoices/pending/${id}/reconcile/push-to-xero`,
      { atlasItemIds }
    ),
};

// Suppliers API endpoints
export const suppliersApi = {
  // List all suppliers with optional filters
  list: (params?: {
    search?: string;
    country?: string;
    active?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/suppliers', { params });
  },

  // Get a specific supplier by ID
  get: (id: string) => {
    return api.get(`/suppliers/${id}`);
  },

  // Create a new supplier
  create: (supplier: {
    code: string;
    name: string;
    country: string;
    address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    phone?: string;
    email?: string;
    contact_person?: string;
    active?: boolean;
    notes?: string;
  }) => {
    return api.post('/suppliers', supplier);
  },

  // Update a supplier
  update: (id: string, updates: Partial<{
    code: string;
    name: string;
    country: string;
    address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    phone?: string;
    email?: string;
    contact_person?: string;
    active?: boolean;
    notes?: string;
  }>) => {
    return api.patch(`/suppliers/${id}`, updates);
  },
};

// Buyers API endpoints
export const buyersApi = {
  // List all buyers with optional filters
  list: (params?: {
    search?: string;
    country?: string;
    active?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/buyers', { params });
  },

  // Get a specific buyer by ID
  get: (id: string) => {
    return api.get(`/buyers/${id}`);
  },

  // Create a new buyer
  create: (buyer: {
    code: string;
    name: string;
    country: string;
    address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    phone?: string;
    email?: string;
    contact_person?: string;
    active?: boolean;
    notes?: string;
  }) => {
    return api.post('/buyers', buyer);
  },

  // Update a buyer
  update: (id: string, updates: Partial<{
    code: string;
    name: string;
    country: string;
    address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    phone?: string;
    email?: string;
    contact_person?: string;
    active?: boolean;
    notes?: string;
  }>) => {
    return api.patch(`/buyers/${id}`, updates);
  },
};

// Sellers API endpoints
export const sellersApi = {
  // List all sellers with optional filters
  list: (params?: {
    search?: string;
    active?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/sellers', { params });
  },

  // Get a specific seller by ID
  get: (id: string) => {
    return api.get(`/sellers/${id}`);
  },

  // Create a new seller
  create: (seller: {
    abbreviation: string;
    name: string;
    address?: string;
    active?: boolean;
  }) => {
    return api.post('/sellers', seller);
  },

  // Update a seller
  update: (id: string, updates: Partial<{
    abbreviation: string;
    name: string;
    address?: string;
    active?: boolean;
  }>) => {
    return api.patch(`/sellers/${id}`, updates);
  },

  // Delete (deactivate) a seller
  delete: (id: string) => {
    return api.delete(`/sellers/${id}`);
  },
};

// Companies API endpoints (unified access to suppliers and buyers)
export const companiesApi = {
  // List companies with type filter
  list: (params?: {
    type?: 'supplier' | 'buyer' | 'all';
    search?: string;
    active?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    return api.get('/companies', { params });
  },

  // Get a specific company by ID and type
  get: (id: string, type: 'supplier' | 'buyer') => {
    return api.get(`/companies/${id}`, { params: { type } });
  },

  // Create a new company
  create: (company: {
    shortCode: string;
    name: string;
    type: 'SUPPLIER' | 'BUYER';
    address?: string;
    country?: string;
    status?: 'ACTIVE' | 'INACTIVE';
    defaultCommissionRate?: number;
    defaultPOD?: string;
    defaultPOL?: string;
    xeroID?: string;
  }) => {
    return api.post('/companies', company);
  },

  // Update a company
  update: (id: string, updates: Partial<{
    shortCode: string;
    name: string;
    type: 'SUPPLIER' | 'BUYER';
    address?: string;
    country?: string;
    status?: 'ACTIVE' | 'INACTIVE';
    defaultCommissionRate?: number;
    defaultPOD?: string;
    defaultPOL?: string;
    xeroID?: string;
  }>) => {
    return api.patch(`/companies/${id}`, updates);
  },
};

// Reports API endpoints
interface OutstandingBySupplierResponse {
  suppliers: Array<{
    supplierId: string;
    supplierName: string;
    supplierCode: string;
    shipmentCount: number;
    totalLbs: number;
    containerCount: number;
    earliestEta: string | null;
    latestEta: string | null;
    shipments: Array<{
      id: string;
      reference: string;
      contractRef: string;
      bookingNumber: string | null;
      etd: string | null;
      eta: string | null;
      status: string;
      totalLbs: number;
      containerCount: number;
    }>;
  }>;
  totals: {
    supplierCount: number;
    shipmentCount: number;
    totalLbs: number;
    containerCount: number;
  };
}

export interface ShipmentsByStatusResponse {
  shipments: Array<{
    id: string;
    reference: string;
    contractRef: string;
    supplierName: string;
    supplierCode: string;
    buyerName: string;
    buyerCode: string;
    start: string | null;
    end: string | null;
    bookingNumber: string | null;
    etd: string | null;
    eta: string | null;
    status: string;
    totalLbs: number;
    containerCount: number;
  }>;
  statusCounts: Record<string, number>;
  allStatuses: string[];
  totals: {
    shipmentCount: number;
    totalLbs: number;
    containerCount: number;
  };
}

export const reportsApi = {
  getOutstandingBySupplier: (params?: { etaFrom?: string; etaTo?: string }): Promise<OutstandingBySupplierResponse> => {
    return api.get('/reports/outstanding-by-supplier', { params }) as Promise<OutstandingBySupplierResponse>;
  },
  getShipmentsByStatus: (params?: { 
    statuses?: string; 
    etaFrom?: string; 
    etaTo?: string;
    rangeFrom?: string;
    rangeTo?: string;
    supplierId?: string;
    buyerId?: string;
  }): Promise<ShipmentsByStatusResponse> => {
    return api.get('/reports/shipments-by-status', { params }) as Promise<ShipmentsByStatusResponse>;
  },
};

export interface DocumentTemplate {
  id: string;
  type: string;
  name: string;
  description: string | null;
  content: string;
  isDefault: boolean;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  versionCount?: number;
  versions?: DocumentTemplateVersion[];
}

export interface DocumentTemplateVersion {
  id: string;
  templateId: string;
  version: number;
  content: string;
  createdAt: string;
  createdBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface TemplateVariables {
  company: { name: string; address: string; phone: string; email: string; website: string };
  logoUrl: string;
  signatureUrl: string;
  supplier: { name: string; shortName: string; address: string; country: string; code: string };
  buyer: { name: string; shortName: string; address: string; country: string; code: string };
  seller: { name: string; address: string } | null;
  contract: {
    reference: string;
    contractDate: string;
    status: string;
    commodity: string;
    hsCode: string;
    paymentTermsSeller: string;
    paymentTermsBuyer: string;
    basisSeller: string;
    basisBuyer: string;
    commissionPercentage: string;
    sellerNotes: string;
    buyerNotes: string;
  };
  shipments: Array<{
    reference: string;
    startDate: string;
    endDate: string;
    portOfLoading: string;
    portOfDischarge: string;
    goods: Array<{
      description: string;
      quantity: number;
      unit: string;
      priceSeller: string;
      priceBuyer: string;
    }>;
  }>;
  hasMultipleShipments: boolean;
  today: string;
}

export const templatesApi = {
  list: (params?: {
    type?: string;
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ templates: DocumentTemplate[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }> => {
    return api.get('/templates', { params });
  },

  get: (id: string): Promise<DocumentTemplate> => {
    return api.get(`/templates/${id}`);
  },

  getByType: (type: string): Promise<DocumentTemplate> => {
    return api.get(`/templates/by-type/${type}`);
  },

  getTypes: (): Promise<{ types: Array<{ value: string; label: string }> }> => {
    return api.get('/templates/types');
  },

  create: (data: {
    type: string;
    name: string;
    description?: string;
    content: string;
    isDefault?: boolean;
    isActive?: boolean;
  }): Promise<DocumentTemplate> => {
    return api.post('/templates', data);
  },

  update: (id: string, data: {
    name?: string;
    description?: string | null;
    content?: string;
    isDefault?: boolean;
    isActive?: boolean;
  }): Promise<DocumentTemplate> => {
    return api.put(`/templates/${id}`, data);
  },

  delete: (id: string): Promise<{ success: boolean; message: string }> => {
    return api.delete(`/templates/${id}`);
  },

  restore: (id: string, version: number): Promise<DocumentTemplate & { message: string }> => {
    return api.post(`/templates/${id}/restore/${version}`);
  },

  generate: (id: string, data: {
    contractId: string;
    shipmentId?: string;
    format?: 'pdf' | 'html';
  }): Promise<Blob | string> => {
    if (data.format === 'html') {
      return api.post(`/templates/${id}/generate`, data);
    }
    return api.post(`/templates/${id}/generate`, data, {
      responseType: 'blob',
    });
  },

  generateByType: (type: string, data: {
    contractId: string;
    shipmentId?: string;
    format?: 'pdf' | 'html';
  }): Promise<Blob | string> => {
    if (data.format === 'html') {
      return api.post(`/templates/generate/${type}`, data);
    }
    return api.post(`/templates/generate/${type}`, data, {
      responseType: 'blob',
    });
  },

  preview: (data: {
    content: string;
    contractId: string;
    shipmentId?: string;
  }): Promise<string> => {
    return api.post('/templates/preview', data);
  },

  getVariables: (contractId: string, shipmentId?: string): Promise<TemplateVariables> => {
    const params = shipmentId ? { shipmentId } : {};
    return api.get(`/templates/variables/${contractId}`, { params });
  },
};

export interface CompanySettings {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  logoUrl: string | null;
  logoDriveId: string | null;
  signatureUrl: string | null;
  signatureDriveId: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  updatedAt: string;
}

interface ImageUploadResult {
  success: boolean;
  logoUrl?: string;
  logoDriveId?: string;
  signatureUrl?: string;
  signatureDriveId?: string;
}

export const settingsApi = {
  getCompany: (): Promise<CompanySettings> => {
    return api.get('/settings/company');
  },

  updateCompany: (data: Partial<Omit<CompanySettings, 'id' | 'updatedAt'>>): Promise<CompanySettings> => {
    return api.put('/settings/company', data);
  },

  uploadLogoFile: (file: File): Promise<ImageUploadResult> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        try {
          const result = await api.post<ImageUploadResult>('/settings/company/upload-logo-direct', {
            imageBase64: base64,
            filename: file.name,
            mimeType: file.type,
          });
          resolve(result as unknown as ImageUploadResult);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  },

  uploadSignatureFile: (file: File): Promise<ImageUploadResult> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        try {
          const result = await api.post<ImageUploadResult>('/settings/company/upload-signature-direct', {
            imageBase64: base64,
            filename: file.name,
            mimeType: file.type,
          });
          resolve(result as unknown as ImageUploadResult);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  },

  setLogoUrl: (logoUrl: string, logoDriveId?: string): Promise<CompanySettings> => {
    return api.post('/settings/company/logo', { logoUrl, logoDriveId });
  },

  setSignatureUrl: (signatureUrl: string, signatureDriveId?: string): Promise<CompanySettings> => {
    return api.post('/settings/company/signature', { signatureUrl, signatureDriveId });
  },

  deleteLogo: (): Promise<{ success: boolean; settings: CompanySettings }> => {
    return api.delete('/settings/company/logo');
  },

  deleteSignature: (): Promise<{ success: boolean; settings: CompanySettings }> => {
    return api.delete('/settings/company/signature');
  },
};

export interface SystemSettings {
  defaultCommissionRate: number;
  defaultCommissionShare: number;
  defaultMarginShare: number;
  defaultPaymentSchedule: string;
}

export const systemSettingsApi = {
  get: (): Promise<SystemSettings> => {
    return api.get('/settings/system');
  },

  update: (data: SystemSettings): Promise<SystemSettings> => {
    return api.put('/settings/system', data);
  },
};

export interface DocumentSnippet {
  id: string;
  name: string;
  label: string;
  description: string | null;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const snippetsApi = {
  list: (params?: {
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ snippets: DocumentSnippet[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } }> => {
    return api.get('/snippets', { params });
  },

  get: (id: string): Promise<DocumentSnippet> => {
    return api.get(`/snippets/${id}`);
  },

  getByName: (name: string): Promise<DocumentSnippet> => {
    return api.get(`/snippets/by-name/${name}`);
  },

  getActive: (): Promise<{ snippets: DocumentSnippet[] }> => {
    return api.get('/snippets/active/all');
  },

  create: (data: {
    name: string;
    label: string;
    description?: string;
    content: string;
    isActive?: boolean;
  }): Promise<DocumentSnippet> => {
    return api.post('/snippets', data);
  },

  update: (id: string, data: {
    name?: string;
    label?: string;
    description?: string | null;
    content?: string;
    isActive?: boolean;
  }): Promise<DocumentSnippet> => {
    return api.put(`/snippets/${id}`, data);
  },

  delete: (id: string): Promise<{ success: boolean; message: string }> => {
    return api.delete(`/snippets/${id}`);
  },
};

export default api;